import { useRef, useState } from "react";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { GALLERY } from "@/veedu/data";
import { ChapterLabel, FadeUp, BackgroundVideo } from "./Shared";

const GALLERY_ITEMS = GALLERY.slice(0, 5);

const SIZES = {
  wide: "w-1/2 md:w-[24vw] aspect-[16/9]",
  landscape: "w-1/2 md:w-[19vw] aspect-[4/3]",
  portrait: "w-[42%] md:w-[13vw] aspect-[3/4]",
  tall: "w-[45%] md:w-[14vw] aspect-[2/3]",
  square: "w-[47%] md:w-[15vw] aspect-square",
};

const TimelineItem = ({ f, i, onOpen }) => {
  const isLeft = i % 2 === 0;
  const isIvory = i % 2 !== 0;

  return (
    <div className={`w-full ${isIvory ? 'bg-material-ivory' : 'bg-material-dark'} border-b ${isIvory ? 'border-dark-brown/5' : 'border-brass/10'}`}>
      <div className="relative mx-auto max-w-7xl flex flex-col md:flex-row items-start md:items-center py-16 md:py-24">
        {/* Timeline Line Segment */}
        <div className={`absolute left-[30px] md:left-1/2 top-0 bottom-0 w-px -translate-x-1/2 ${isIvory ? 'bg-dark-brown/10' : 'bg-cream/10'}`} />

        {/* Mobile Dot */}
        <div className="absolute left-[30px] md:hidden top-1/2 -translate-y-1/2 z-10 flex items-center">
          <motion.div 
            initial={{ opacity: 0.3, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: f.featured ? 1.4 : 1 }}
            viewport={{ margin: "-20% 0px -20% 0px" }}
            className={`w-2 h-2 rounded-full ${isIvory ? 'bg-dark-brown' : 'bg-brass'} -translate-x-1/2`}
          />
        </div>
        <div className={`absolute left-[45px] md:hidden top-1/2 -translate-y-1/2 z-10 py-2 ${isIvory ? 'bg-ivory' : 'bg-ink'}`}>
          <span className={`font-body text-[10px] tracking-[0.35em] ${isIvory ? 'text-dark-brown/70' : 'text-brass'}`}>
             {String(i + 1).padStart(2, "0")}
          </span>
        </div>

        {/* Desktop Dot */}
        <motion.div 
          initial={{ opacity: 0.3, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: f.featured ? 1.4 : 1 }}
          viewport={{ margin: "-20% 0px -20% 0px" }}
          className={`hidden md:block absolute left-1/2 top-1/2 w-2 h-2 rounded-full ${isIvory ? 'bg-dark-brown' : 'bg-brass'} -translate-x-1/2 -translate-y-1/2 z-10`}
        />

      <div className={`w-full flex flex-col pl-[60px] pr-6 md:px-0 md:flex-row ${isLeft ? '' : 'md:flex-row-reverse'} gap-8 md:gap-0`}>
        {/* Media */}
        <div className={`flex-1 flex ${isLeft ? 'md:justify-end' : 'md:justify-start'}`}>
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <button
              onClick={() => onOpen(i)}
              className={`group relative block border p-2 shadow-2xl overflow-hidden max-w-full ${
                isIvory ? 'border-dark-brown/10 bg-ivory shadow-dark-brown/10' : 'border-brass/20 bg-[#151009] shadow-black/70'
              }`}
            >
              <span className={`block overflow-hidden ${SIZES[f.ar]}`}>
                {f.video ? (
                  <BackgroundVideo
                    src={f.video}
                    poster={f.poster}
                    autoPlay
                    className="h-full w-full object-cover transition-all duration-1000 group-hover:scale-105"
                  />
                ) : (
                  <img
                    src={f.img}
                    alt={f.caption}
                    loading="lazy"
                    className="h-full w-full object-cover transition-all duration-1000 group-hover:scale-105"
                  />
                )}
              </span>
              {f.video && (
                <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full border border-brass/60 bg-ink/40 backdrop-blur-sm transition-transform duration-500 group-hover:scale-110">
                    <Play size={16} className="text-ivory ml-1" />
                  </span>
                </span>
              )}
              <span className={`absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-500 group-hover:opacity-100 backdrop-blur-[2px] ${isIvory ? 'bg-ivory/20' : 'bg-ink/20'}`}>
                <span className={`font-body text-[9px] tracking-[0.35em] uppercase ${isIvory ? 'text-dark-brown' : 'text-ivory'}`}>View</span>
              </span>
            </button>
          </motion.div>
        </div>

        {/* Desktop Spacer/Number */}
        <div className="hidden md:flex w-40 shrink-0 items-center justify-center relative z-20">
          <span className={`font-body text-[10px] tracking-[0.35em] px-3 py-4 ${isIvory ? 'bg-ivory text-dark-brown/70' : 'bg-ink text-brass'}`}>
            {String(i + 1).padStart(2, "0")}
          </span>
        </div>

        {/* Text */}
        <div className={`flex-1 flex flex-col justify-center ${isLeft ? 'md:items-start md:text-left' : 'md:items-end md:text-right'}`}>
          <motion.div 
            initial={{ opacity: 0, x: isLeft ? -15 : 15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className={`flex flex-col ${isLeft ? 'md:items-start' : 'md:items-end'}`}
          >
            {f.label && (
              <span className={`font-body text-[8px] tracking-[0.4em] mb-3 uppercase ${isIvory ? 'text-dark-brown' : 'text-brass'}`}>
                {f.label}
              </span>
            )}
            <h3 className={`font-display text-2xl md:text-3xl tracking-tight mb-2 ${isIvory ? 'text-dark-brown' : 'text-ivory'}`}>
              {f.caption}
            </h3>
            <p className={`font-body font-light text-sm max-w-[280px] italic ${isIvory ? 'text-dark-brown/70' : 'text-cream/60'}`}>
              {f.desc}
            </p>
          </motion.div>
        </div>
      </div>
      </div>
    </div>
  );
};

const Gallery = () => {
  const [open, setOpen] = useState(null);

  return (
    <section 
      id="gallery" 
      data-testid="gallery-section" 
      className="relative overflow-hidden w-full"
    >
      {/* Intro */}
      <div className="bg-material-dark pt-32 pb-20">
        <div className="mx-auto max-w-6xl px-6 md:px-16 md:pt-8 text-center">
          <ChapterLabel num="04" name="Perfect for Every Occasion" mal="ചിത്രങ്ങൾ" center />
          <h2 className="mt-12 font-display text-4xl leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl uppercase text-ivory">
            <FadeUp delay={0.1}>THE STORY OF</FadeUp>
            <FadeUp delay={0.25}>
              <em className="text-brass">THE VEEDU.</em>
            </FadeUp>
          </h2>
          <FadeUp delay={0.4}>
            <p className="mt-6 mx-auto max-w-md font-body text-sm font-light md:text-base text-cream/60">
              Food, people, celebrations and moments from the house.
            </p>
          </FadeUp>
        </div>
      </div>

      {/* Timeline Wrapper */}
      <div className="flex flex-col">
        {GALLERY_ITEMS.map((f, i) => (
          <TimelineItem key={i} f={f} i={i} onOpen={setOpen} />
        ))}
      </div>

      {/* Outro */}
      <div className="bg-material-dark py-32">
        <div className="relative z-10 mx-auto max-w-xl text-center px-6">
          <FadeUp>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-4 uppercase text-ivory">
              The house <em className="text-brass">keeps going.</em>
            </h3>
            <p className="font-body font-light text-sm text-cream/50">
              And the story continues...<br />every day, at the Veedu.
            </p>
          </FadeUp>
        </div>
      </div>

      {/* Viewer */}
      <AnimatePresence>
        {open !== null && (
          <motion.div
            data-testid="gallery-viewer"
            className="fixed inset-0 z-[175] flex flex-col bg-ink/[0.985] backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex items-center justify-between px-6 py-6 md:px-10">
              <span className="font-body text-[10px] tracking-[0.4em] uppercase text-cream/50">
                {String(open + 1).padStart(2, "0")} / {String(GALLERY_ITEMS.length).padStart(2, "0")}
              </span>
              <button
                data-testid="viewer-close-btn"
                onClick={() => setOpen(null)}
                aria-label="Close viewer"
                className="text-cream/70 transition-colors hover:text-brass"
              >
                <X size={24} strokeWidth={1.25} />
              </button>
            </div>
            <div className="relative flex flex-1 items-center justify-center overflow-hidden px-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={open}
                  className="flex max-h-full flex-col items-center"
                  initial={{ opacity: 0, scale: 0.97, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.99, y: -10 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="relative max-h-[70vh] max-w-full overflow-hidden shadow-2xl shadow-black/80">
                    {GALLERY_ITEMS[open].video ? (
                      <video
                        src={GALLERY_ITEMS[open].video}
                        poster={GALLERY_ITEMS[open].poster}
                        autoPlay
                        controls
                        loop
                        playsInline
                        className="max-h-[70vh] w-auto max-w-full object-contain"
                      />
                    ) : (
                      <img
                        src={GALLERY_ITEMS[open].img}
                        alt={GALLERY_ITEMS[open].caption}
                        className="max-h-[70vh] w-auto max-w-full object-contain"
                      />
                    )}
                  </div>
                  <div className="mt-6 text-center">
                    <p className="font-body text-[10px] tracking-[0.45em] uppercase text-brass mb-2">
                      {GALLERY_ITEMS[open].caption}
                    </p>
                    <p className="font-mal font-light text-xs text-cream/60 italic">
                      {GALLERY_ITEMS[open].desc}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex items-center justify-center gap-6 pb-10">
              <button
                data-testid="viewer-prev-btn"
                onClick={() => setOpen((open - 1 + GALLERY_ITEMS.length) % GALLERY_ITEMS.length)}
                aria-label="Previous item"
                className="flex h-14 w-14 items-center justify-center rounded-full border border-cream/10 text-cream/50 transition-all duration-300 hover:border-brass hover:text-brass"
              >
                <ChevronLeft size={20} strokeWidth={1} />
              </button>
              <button
                data-testid="viewer-next-btn"
                onClick={() => setOpen((open + 1) % GALLERY_ITEMS.length)}
                aria-label="Next item"
                className="flex h-14 w-14 items-center justify-center rounded-full border border-cream/10 text-cream/50 transition-all duration-300 hover:border-brass hover:text-brass"
              >
                <ChevronRight size={20} strokeWidth={1} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
